import Moment from 'moment';

// React
import { Link } from 'react-router-dom';

// Prime React
import { Tooltip } from 'primereact/tooltip';

/**
 * @returns The url for the headless application
 */
export function getUrl() {
	return 'https://planner-api.cellpacksolutions.co.uk';
}

/**
 * Get HTML for the product information
 * 
 * @param {Object} rowData The data associated to the row
 * @returns {html} Product information with a tooltip description
 */
export function getProductHTML(product_id, product_sku, product_description, capabilities, product_qty = 1) {
	if(capabilities.read_products) {
			return <>
				<Tooltip autoHide={false} target={".product_tooltip"} />
				<div className={"product_tooltip"} data-pr-tooltip={product_description} data-pr-position="left"><Link to={"/product/" + product_id}>{product_sku}</Link> <span class="badge badge-primary bg-primary">x {product_qty}</span></div>
			</>;
	} else {
		return <>
			<Tooltip autoHide={false} target={".product_tooltip"} />
			<div className={"product_tooltip"} data-pr-tooltip={product_description} data-pr-position="left">{product_sku}</div>
		</>;
	}
}

/**
 * Get link for the employee
 * 
 * @param {Object} rowData The data associated to the row
 * @returns {html} Product information with a tooltip description
 */
export function getEmployeeLink(employee_id, employee_name, capabilities) {
	if(capabilities.read_employees) {
		return <Link to={'/employee/' + employee_id}>{employee_name}</Link>
	} else {
		return employee_name;
	}
}

/**
 * Get link for the traveller
 * 
 * @param {Object} rowData The data associated to the row
 * @returns {html} Product information with a tooltip description
 */
export function getTravellerLink(traveller_id, traveller_number, capabilities) {
	if(capabilities.read_travellers) {
		return <Link to={"/traveller/" + traveller_id}>{traveller_number}</Link>
	} else {
		return traveller_number;
	}
}

/**
 * Get link for the traveller
 * 
 * @param {Object} rowData The data associated to the row
 * @returns {html} Product information with a tooltip description
 */
export function getOrderLink(order_id, order_number, capabilities) {
	if(capabilities.read_shop_orders) {
		return <Link to={"/job/" + order_id}>{order_number}</Link>
	} else {
		return order_number;
	}
}

export function getEditButton(url, isLocked = false) {
	// Check if the current row is locked, change the icon and text based on if it is or not
	let icon = <i className="pi pi-pencil me-1" style={{ fontSize: '0.8rem' }}></i>;
	let text = 'Edit';

	if(isLocked === true) {
		icon = <i className="pi pi-lock me-1" style={{ fontSize: '0.8rem' }}></i>;
		text = 'View';
	}

	return (<div style={{textAlign: 'right'}}><Link to={url} className="btn btn-primary">{icon} {text}</Link></div>);
}

/**
 * Function which outputs the time formatting for the system
 * 
 * @param {int} time The number of minutes
 * @param {String} unit The unit of time that should be outputted
 * 
 * @returns {String} The formatted time
 */
export function formatTime(time, unit) {
	let formattedTime;

	if(unit === 'hour') {
		let hours = Math.round(time / 60);

		formattedTime = hours + ' Hour' + (hours !== 1 ? 's' : '');
	} else if(unit === 'minute') {
		let minutes = Math.round(time / 60);

		formattedTime = minutes + ' Minute' + (minutes !== 1 ? 's' : '');
	} else {
		formattedTime = time;
	}

	return formattedTime;
}

/**
 * @returns The date formatting the system should use
 */
export function getDateFormat() {
	return 'DD/MM/YYYY';
}

/**
 * @returns Adds the timing formatting the system uses onto the date format
 */
export function getDateTimeFormat() {
	return getDateFormat() + ' @ HH:mm:ss';
}

/**
 * @returns Adds the timing formatting the system uses onto the date format
 */
export function getISODateTimeFormat() {
	return 'YYYY-MM-DDTHH:mm:ss';
}

export function getJobTimeFormat(totalSeconds = 0) {
	let isNeg = false;


	if(totalSeconds < 0) {
		totalSeconds = Math.abs(totalSeconds);
		isNeg = true;
	}

	let hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
	let minutes = (Math.floor(totalSeconds / 60) % 60).toString().padStart(2, '0');
	let seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');

	if(isNaN(hours)) {
		hours = '00';
	}

	if(isNaN(minutes)) {
		minutes = '00';
	}

	if(isNaN(seconds)) {
		seconds = '00';
	}



	return (isNeg ? '-' : '') + hours + ':' + minutes + ':' + seconds;
}

export function getJobTimeBadge(actualTime, targetTime, defaultText = 'Not started', forceClass = null) {
	let badgeClass = 'bg-dark';
	let text       = defaultText;
	
	if(actualTime > 0) {
		text = getJobTimeFormat(actualTime);
		badgeClass = 'bg-success';
	} else {
		text = getJobTimeFormat(actualTime);
	}

	if(targetTime) {
		if(actualTime > targetTime) {
			badgeClass = 'bg-warning';
		}

		if(actualTime > (targetTime + ((targetTime / 100) * 5))) {
			badgeClass = 'bg-danger';
		}
	} else {
		badgeClass = 'bg-dark';
	}
	
	if(forceClass) {
		badgeClass = forceClass;
	}

	return <span className={badgeClass + " badge text-white"}><i className="pi pi-stopwatch me-1" style={{ fontSize: '0.65rem' }} ></i>{text}</span>
}

/**
 * A function to format the date returned by the REST API and if the date should be highlighted or not if it has passed
 * 
 * @param {String} date The date to be formatted
 * @param {Boolean} highlight A boolean if the date should be highlighted if after the current date
 * @param {Boolean} time A boolean to include the time as well as the date
 * 
 * @returns HTML of the current date in the correct format
 */
export function formatDate(date, time = false, highlight = false, invalidDateText = 'No Date Set') {
	if(date) {
		let dateFormat = getDateFormat();

		if(time) {
			dateFormat = getDateTimeFormat();
		}

		let formattedDate = Moment(date).format(dateFormat);

		if(formattedDate === 'Invalid date') {
			return invalidDateText;
		}

		if(Moment(date).isBefore(Moment()) && highlight) {
			return <span className="badge bg-danger text-white">{formattedDate}</span>
		} else {
			return formattedDate;
		}
	}
}

/**
 * A function to format the date returned by the REST API and if the date should be highlighted or not if it has passed
 *
 * @param {String} date The date to be formatted
 *
 * @returns HTML of the current date in the correct format
 */
export function formatTrafficDate(date, status = 'inputted', forceClass = '') {
	let isCompleted = false;

	if(status === 'Completed') {
		isCompleted = true;
	}

	if(date) {
		let dateFormat = getDateFormat();

		let formattedDate = Moment(date.replace("\/", "/")).format(dateFormat);

		if(formattedDate === 'Invalid date') {
			return 'No Date Set';
		}

		if(Moment(date).isBefore(Moment()) && isCompleted === false && forceClass == '') {
			if(Moment(date).add(1, "days").isAfter(Moment()) && isCompleted === false && forceClass == '') {
				return <span className="badge bg-warning text-white">{formattedDate}</span>;
			} else {
				return <span className="badge bg-danger text-white">{formattedDate}</span>
			}
		} else {
			if(forceClass) {
				return <span className={"badge " + forceClass + " text-white"}>{formattedDate}</span>;
			} else {
				return <span className="badge bg-success text-white">{formattedDate}</span>;
			}
		}
	}
}